import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { UseMutationOptions, useQueryClient, useMutation } from 'react-query'
import { BrandVisualEnhancement } from 'types'
import { cacheKeys } from './cacheKeys'

interface Data {
  brand_visual_enhancement: BrandVisualEnhancement
}

interface MutationContext {
  previousData: Data | undefined
  queryKey: readonly unknown[]
}

export const destroyBrandVisualEnhancement = async ({
  workspaceId,
  brandVisualKitId,
  id,
  signal,
}: {
  workspaceId: number
  brandVisualKitId: number
  id: number
  signal?: AbortSignal
}) => {
  const res = await sendToApi<Data>(
    `/w/${workspaceId}/brand_visual_kits/${brandVisualKitId}/brand_visual_enhancements/${id}`,
    {
      method: 'DELETE',
      signal,
    },
  )

  if (res.type !== SUCCESS_RESPONSE_TYPE) throw new Error(res.data)

  return res.data
}

export const useDestroyBrandVisualEnhancement = (
  brandVisualKitId: number | undefined,
  options?: UseMutationOptions<Data, unknown, { id: number }, MutationContext>,
) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, unknown, { id: number }, MutationContext>(
    ({ id }) => {
      if (!brandVisualKitId) throw new Error('brandVisualKitId is required')
      return destroyBrandVisualEnhancement({ workspaceId, brandVisualKitId, id })
    },
    {
      ...options,
      onMutate: async ({ id }): Promise<MutationContext | undefined> => {
        if (!brandVisualKitId) return

        const queryKey = cacheKeys.detail(workspaceId, brandVisualKitId, id)

        await queryClient.cancelQueries({ queryKey })

        const previousData = queryClient.getQueryData<Data>(queryKey)

        queryClient.removeQueries({ queryKey, exact: true })

        return { previousData, queryKey }
      },
      onError: (error, variables, context) => {
        if (context?.previousData && context?.queryKey) {
          queryClient.setQueryData(context.queryKey, context.previousData)
        }
        options?.onError?.(error, variables, context)
      },
    },
  )
}
